import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { PASSWORD_POLICY, COMMON_PASSWORDS } from './password-policy';

/**
 * Resultado granular de validación.
 * Cada flag = true significa que ESA regla específica falla.
 * El componente de fuerza lo usa para mostrar los items en rojo/verde.
 */
export interface PasswordPolicyErrors {
    tooShort?:        boolean;
    tooLong?:         boolean;
    noUppercase?:     boolean;
    noLowercase?:     boolean;
    noNumber?:        boolean;
    noSymbol?:        boolean;
    repeatedChars?:   boolean;   // 3+ chars iguales seguidos
    hasSequence?:     boolean;   // abc, 123, qwerty
    containsEmail?:   boolean;
    containsName?:    boolean;
    isCommon?:        boolean;
}

export interface PasswordPolicyResult {
    valid:  boolean;
    errors: PasswordPolicyErrors;
}

/**
 * Validador puro de la política. Sirve tanto para Reactive Forms (vía wrapper)
 * como para validar manualmente desde código.
 */
export function validatePassword(
    password: string | null | undefined,
    context: { email?: string; name?: string } = {},
): PasswordPolicyResult {
    const errors: PasswordPolicyErrors = {};

    if (!password) {
        return { valid: false, errors: { tooShort: true } };
    }

    const p = password;

    // Longitud
    if (p.length < PASSWORD_POLICY.minLength) errors.tooShort = true;
    if (p.length > PASSWORD_POLICY.maxLength) errors.tooLong  = true;

    // Categorías
    if (PASSWORD_POLICY.requireUppercase && !/[A-Z]/.test(p)) errors.noUppercase = true;
    if (PASSWORD_POLICY.requireLowercase && !/[a-z]/.test(p)) errors.noLowercase = true;
    if (PASSWORD_POLICY.requireNumber    && !/[0-9]/.test(p)) errors.noNumber    = true;
    if (PASSWORD_POLICY.requireSymbol) {
        // Se compara carácter por carácter en vez de armar una clase de
        // regex. La versión anterior hacía:
        //     new RegExp('[' + escapeRegex(symbolChars) + ']')
        // y `escapeRegex` no escapa el guion, así que la subcadena `+-=`
        // de `symbolChars` se interpretaba como el RANGO de `+` (43) a `=`
        // (61) — que incluye TODOS los dígitos, más `:`, `;` y `<`.
        //
        // Resultado: cualquier contraseña con un número pasaba el
        // requisito de símbolo. `Medtronic2026` se daba por válida. La
        // política quedaba en la práctica reducida de cuatro categorías a
        // tres, sin que nada lo indicara.
        const symbols = new Set(Array.from(PASSWORD_POLICY.symbolChars));
        if (!Array.from(p).some(c => symbols.has(c))) errors.noSymbol = true;
    }

    // Repeticiones (aaa, 111)
    if (PASSWORD_POLICY.blockRepeatedChars && /(.)\1{2,}/.test(p)) {
        errors.repeatedChars = true;
    }

    // Secuencias (abc, 123, qwerty, qaz, etc.)
    if (PASSWORD_POLICY.blockSequences && containsSequence(p)) {
        errors.hasSequence = true;
    }

    // Coincidencia con email
    if (PASSWORD_POLICY.blockEmailMatch && context.email) {
        const localPart = context.email.split('@')[0];
        if (localPart && localPart.length >= 3 &&
            p.toLowerCase().includes(localPart.toLowerCase())) {
            errors.containsEmail = true;
        }
    }

    // Coincidencia con nombre (cualquier palabra del nombre de >=3 chars)
    if (PASSWORD_POLICY.blockNameMatch && context.name) {
        const words = context.name.split(/\s+/).filter(w => w.length >= 3);
        for (const word of words) {
            if (p.toLowerCase().includes(word.toLowerCase())) {
                errors.containsName = true;
                break;
            }
        }
    }

    // Lista de comunes
    if (PASSWORD_POLICY.blockCommonPasswords) {
        const lower = p.toLowerCase();
        if (COMMON_PASSWORDS.some(c => c.toLowerCase() === lower)) {
            errors.isCommon = true;
        }
    }

    return {
        valid:  Object.keys(errors).length === 0,
        errors,
    };
}

/**
 * Wrapper para Reactive Forms.
 *
 * Uso:
 *   password: ['', [Validators.required, passwordPolicyValidator(() => ({
 *     email: this.someEmail,
 *     name:  this.confirmPasswordForm.value.name,
 *   }))]]
 *
 * El callback se ejecuta en cada validación, así que email/name pueden cambiar.
 */
export function passwordPolicyValidator(
    contextProvider: () => { email?: string; name?: string } = () => ({}),
): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const result = validatePassword(control.value, contextProvider());
        return result.valid ? null : { passwordPolicy: result.errors };
    };
}

/**
 * Validador para confirmar que dos campos (password y confirmPassword)
 * tienen el mismo valor.
 */
export function passwordMatchValidator(
    passwordField = 'password',
    confirmField  = 'confirmPassword',
): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
        const passCtrl    = group.get(passwordField);
        const confirmCtrl = group.get(confirmField);
        const pass    = passCtrl?.value;
        const confirm = confirmCtrl?.value;

        // Aún no escriben la confirmación — no marcar error todavía.
        if (!confirm) {
            limpiarMismatch(confirmCtrl);
            return null;
        }

        if (pass === confirm) {
            limpiarMismatch(confirmCtrl);
            return null;
        }

        // El error se marca TAMBIÉN en el control de confirmación, no solo
        // en el grupo. Angular Material solo pinta un `<mat-error>` cuando
        // el CONTROL del campo está en estado de error; con el error
        // únicamente en el FormGroup, el mensaje nunca se mostraba aunque
        // el formulario sí quedara inválido y el botón deshabilitado. El
        // usuario veía el botón apagado sin saber por qué.
        if (confirmCtrl) {
            confirmCtrl.setErrors({ ...(confirmCtrl.errors ?? {}), mismatch: true });
        }
        return { mismatch: true };
    };
}

/**
 * Quita el error `mismatch` del control sin pisar los demás (por ejemplo
 * `required`). Si no queda ninguno, se limpia a `null`.
 */
function limpiarMismatch(control: AbstractControl | null): void {
    if (!control?.hasError('mismatch')) return;
    const restantes = { ...control.errors };
    delete restantes['mismatch'];
    control.setErrors(Object.keys(restantes).length ? restantes : null);
}

/**
 * Calcula la "fuerza" de una contraseña en escala 0-4.
 *  0 = Vacío
 *  1 = Muy débil
 *  2 = Débil
 *  3 = Buena
 *  4 = Fuerte
 *
 * Esto es independiente de la política. Una contraseña puede ser "fuerte"
 * pero seguir incumpliendo una regla específica (ej: contiene el email).
 */
export function passwordStrength(password: string | null | undefined): number {
    if (!password) return 0;

    let score = 0;

    // Longitud
    if (password.length >= 8)  score++;
    if (password.length >= 12) score++;
    if (password.length >= 16) score++;

    // Variedad de caracteres
    let variety = 0;
    if (/[a-z]/.test(password)) variety++;
    if (/[A-Z]/.test(password)) variety++;
    if (/[0-9]/.test(password)) variety++;
    if (/[^a-zA-Z0-9]/.test(password)) variety++;
    if (variety >= 3) score++;
    if (variety === 4) score++;

    // Penalización por repetición o secuencia (si tiene cosas predecibles, baja)
    if (/(.)\1{2,}/.test(password) || containsSequence(password)) score--;

    return Math.min(4, Math.max(0, score));
}

// ─── Helpers privados ────────────────────────────────────────────────────────

function escapeRegex(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Detecta secuencias predecibles (asc o desc) de 4+ caracteres en
 * el alfabeto, números, o filas de teclado QWERTY.
 */
function containsSequence(p: string): boolean {
    const lower = p.toLowerCase();
    const SEQUENCES = [
        'abcdefghijklmnopqrstuvwxyz',
        '0123456789',
        'qwertyuiop',
        'asdfghjkl',
        'zxcvbnm',
        '1qaz2wsx3edc4rfv',
    ];

    for (const seq of SEQUENCES) {
        for (let i = 0; i <= seq.length - 4; i++) {
            const fragment = seq.slice(i, i + 4);
            if (lower.includes(fragment) || lower.includes(reverseString(fragment))) {
                return true;
            }
        }
    }
    return false;
}

function reverseString(s: string): string {
    return s.split('').reverse().join('');
}